import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { toYmd } from "../calendar/utils/calendar-range";
import { ClosedDay } from "./models/ClosedDay";
import { selectClosedDays } from "./closed-days-slice";
import {
  fetchClosedDays,
  markDayClosed,
  reopenDay,
} from "./slice-extensions/closed-days-slice-extension";

interface ManageDayState {
  day: Date;
  closed?: ClosedDay;
}

/**
 * Closed-days controller for the calendar: loads the markers for the visible range, exposes a
 * date→marker lookup, and drives the manage dialog (close with optional occurrence cancellation,
 * or reopen). Calls onOccurrencesChanged when a closure actually cancelled occurrences so the
 * caller can refresh the calendar.
 */
export const useClosedDays = (from: string, to: string, onOccurrencesChanged: () => void) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const closedDays = useAppSelector(selectClosedDays);
  const [manage, setManage] = useState<ManageDayState | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    dispatch(fetchClosedDays({ from, to }));
  }, [dispatch, from, to]);

  const closedByDate = useMemo(() => {
    const map = new Map<string, ClosedDay>();
    for (const day of closedDays) {
      map.set(day.date, day);
    }
    return map;
  }, [closedDays]);

  const openManage = useCallback((day: Date, closed?: ClosedDay) => setManage({ day, closed }), []);
  const closeManage = useCallback(() => setManage(null), []);

  const submitClose = useCallback(
    async (reason: string, cancelOccurrences: boolean, endDate: string | null = null) => {
      if (!manage) return;
      setBusy(true);
      try {
        const result = await dispatch(
          markDayClosed({
            date: toYmd(manage.day),
            endDate,
            reason: reason.trim() || null,
            cancelOccurrences,
          })
        );
        if (markDayClosed.fulfilled.match(result)) {
          const { closedDays, occurrencesCancelled, occurrencesSkipped } = result.payload;
          toast.success(
            endDate
              ? t("calendar.closedDay.toastClosedRange", {
                  days: closedDays.length,
                  cancelled: occurrencesCancelled,
                  skipped: occurrencesSkipped,
                })
              : t("calendar.closedDay.toastClosed", {
                  cancelled: occurrencesCancelled,
                  skipped: occurrencesSkipped,
                })
          );
          if (occurrencesCancelled > 0) {
            onOccurrencesChanged();
          }
          setManage(null);
        } else {
          toast.error(result.payload?.message || t("calendar.closedDay.closeFailed"));
        }
      } finally {
        setBusy(false);
      }
    },
    [dispatch, manage, onOccurrencesChanged, t]
  );

  const submitReopen = useCallback(async () => {
    if (!manage?.closed) return;
    setBusy(true);
    try {
      const result = await dispatch(reopenDay(manage.closed.id));
      if (reopenDay.fulfilled.match(result)) {
        toast.success(t("calendar.closedDay.toastReopened"));
        setManage(null);
      } else {
        toast.error(result.payload?.message || t("calendar.closedDay.reopenFailed"));
      }
    } finally {
      setBusy(false);
    }
  }, [dispatch, manage, t]);

  return { closedByDate, manage, busy, openManage, closeManage, submitClose, submitReopen };
};
